import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Observable, of } from 'rxjs';
import { concatMap, map, reduce } from 'rxjs/operators';
import { Tpl3plService, Shipment } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { RawResponseModalComponent } from '../shared/raw-response-modal/raw-response-modal.component';

@Component({
	selector: 'app-tpl3pl-shipments',
	templateUrl: './tpl3pl-shipments.component.html',
	styleUrls: ['./tpl3pl-shipments.component.scss']
})
export class Tpl3plShipmentsComponent implements OnInit {
	displayedColumns: string[] = ['orderno', 'tracking_number', 'carrier', 'service_type', 'ship_date', 'delivery_date', 'status', 'processed', 'actions'];
	shipments: Shipment[] = [];
	shipmentsTotal = 0;
	pageIndex = 0;
	pageSize = 50;
	readonly pageSizeOptions = [25, 50, 100, 250];
	expandedShipmentId: number | null = null;
	loading = false;
	rawLoading = false;
	ordernoFilter = '';
	private static twoWeeksAgo(): Date {
		const d = new Date();
		d.setDate(d.getDate() - 14);
		return d;
	}
	pollDateStart: Date = Tpl3plShipmentsComponent.twoWeeksAgo();
	pollDateEnd: Date = new Date();
	singleOrderToCheck = '';
	checkOrderChannel: 'b2b' | 'b2c' | 'both' = 'both';
	checkOrderLoading = false;
	lastPollResult: any = null;
	pollStatusMessage = '';

	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private route: ActivatedRoute,
		private dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.route.queryParamMap.subscribe(params => {
			const orderFromRoute = params.get('orderno');
			if (orderFromRoute !== null) {
				this.ordernoFilter = orderFromRoute;
			}
			this.loadShipments();
		});
	}

	loadShipments(): void {
		this.loading = true;
		const filters: any = { limit: this.pageSize, offset: this.pageIndex * this.pageSize };
		if (this.ordernoFilter) {
			filters.orderno = this.ordernoFilter;
		}

		this.tpl3plService.getShipments(filters).subscribe({
			next: (result) => {
				this.shipments = result.data;
				this.shipmentsTotal = result.total ?? 0;
				this.loading = false;
			},
			error: (error) => {
				this.globalSearchService.showNotification('Error loading shipments', 'danger', 'bottom', 'right');
				this.loading = false;
			}
		});
	}

	private formatDate(d: any): string {
		const date = d instanceof Date ? d : new Date(d);
		const y = date.getFullYear();
		const m = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	private formatDateRange(): { from: string; to: string } {
		return {
			from: this.formatDate(this.pollDateStart),
			to: this.formatDate(this.pollDateEnd)
		};
	}

	private getDatesInRange(from: string, to: string): string[] {
		const dates: string[] = [];
		const start = new Date(from);
		const end = new Date(to);
		if (start > end) {
			return [];
		}
		const cursor = new Date(start);
		while (cursor <= end) {
			dates.push(this.formatDate(cursor));
			cursor.setDate(cursor.getDate() + 1);
		}
		return dates;
	}

	pollShipments(): void {
		const { from, to } = this.formatDateRange();
		const dates = this.getDatesInRange(from, to);
		if (dates.length === 0) {
			this.globalSearchService.showNotification('Invalid date range: from must be on or before to', 'warning', 'bottom', 'right');
			return;
		}
		this.loading = true;
		this.lastPollResult = null;
		this.pollStatusMessage = dates.length === 1 ? `Polling ${dates[0]}…` : `Polling ${dates.length} days (${from} – ${to})…`;

		const pollOne = (date: string): Observable<any> =>
			this.tpl3plService.pollShipments(date, date);

		of(...dates).pipe(
			concatMap((date, index) => {
				this.pollStatusMessage = dates.length === 1
					? `Polling ${date}…`
					: `Polling day ${index + 1} of ${dates.length}: ${date}…`;
				return pollOne(date);
			}),
			reduce(
				(acc: { processed: number; success: boolean; message: string; response: any }, result) => {
					const ok = result && (result.success === true || (result.success !== false && result.response_b2b?.code === 200));
					const processed = result?.processed ?? (Array.isArray(result?.data) ? result.data.length : 0);
					acc.processed += processed;
					if (!ok) {
						acc.success = false;
						acc.message = result?.message ?? 'One or more days failed';
						acc.response = result?.response ?? result?.response_b2b ?? acc.response;
					}
					return acc;
				},
				{ processed: 0, success: true, message: '', response: null }
			)
		).subscribe({
			next: (agg) => {
				this.pollStatusMessage = '';
				this.lastPollResult = {
					success: agg.success,
					processed: agg.processed,
					message: agg.success
						? (dates.length === 1 ? (agg.processed ? `${agg.processed} shipment(s) processed` : 'No shipments for this date')
							: `Polled ${dates.length} days, ${agg.processed} shipment(s) processed`)
						: agg.message,
					response: agg.response
				};
				if (agg.success) {
					this.globalSearchService.showNotification(
						`Processed ${agg.processed} shipments from ${dates.length} day(s)`,
						'success', 'bottom', 'right'
					);
					this.pageIndex = 0;
					this.loadShipments();
				} else {
					this.globalSearchService.showNotification('Poll failed: ' + agg.message, 'danger', 'bottom', 'right');
				}
				this.loading = false;
			},
			error: (error) => {
				this.pollStatusMessage = '';
				this.lastPollResult = {
					success: false,
					message: error?.error?.message || error?.message || 'Error polling shipments',
					response: error?.error?.response ?? error?.error
				};
				this.globalSearchService.showNotification('Error polling shipments', 'danger', 'bottom', 'right');
				this.loading = false;
			}
		});
	}

	onPage(event: { pageIndex: number; pageSize: number }): void {
		this.pageIndex = event.pageIndex;
		this.pageSize = event.pageSize;
		this.loadShipments();
	}

	toggleExpand(shipment: Shipment): void {
		this.expandedShipmentId = this.expandedShipmentId === shipment.id ? null : shipment.id;
	}

	phaseLabel(phase: string | undefined): string {
		if (!phase) return 'Pre-invoice';
		const labels: Record<string, string> = {
			pre_invoice: 'Pre-invoice',
			auto_invoiced: 'Auto-invoiced',
			invoice_failed: 'Invoice failed'
		};
		return labels[phase] || phase;
	}

	checkSingleOrder(): void {
		const orderNo = (this.singleOrderToCheck || '').trim();
		if (!orderNo) {
			this.globalSearchService.showNotification('Enter an order number to check', 'warning', 'bottom', 'right');
			return;
		}
		this.checkOrderLoading = true;
		this.lastPollResult = null;
		this.tpl3plService.pollShipmentsByOrderNo(orderNo, this.checkOrderChannel).subscribe({
			next: (result) => {
				this.lastPollResult = result;
				this.checkOrderLoading = false;
				if (result.success) {
					const channelLabel = (result.channel === 'b2b' || result.channel === 'b2c') ? ` (${result.channel.toUpperCase()})` : '';
					this.globalSearchService.showNotification(
						result.processed ? `Processed ${result.processed} shipment(s) for order ${orderNo}${channelLabel}` : (result.message || 'Done'),
						'success', 'bottom', 'right'
					);
					this.ordernoFilter = orderNo;
					this.pageIndex = 0;
					this.loadShipments();
				} else {
					this.globalSearchService.showNotification('Check failed: ' + (result.message || 'Unknown error'), 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.checkOrderLoading = false;
				this.lastPollResult = { success: false, message: err?.error?.message || err?.message || 'Error checking order' };
				this.globalSearchService.showNotification('Error checking order', 'danger', 'bottom', 'right');
			}
		});
	}

	openRawRequestResponse(): void {
		const { from, to } = this.formatDateRange();
		const dates = this.getDatesInRange(from, to);
		if (dates.length === 0) {
			this.globalSearchService.showNotification('Invalid date range', 'warning', 'bottom', 'right');
			return;
		}
		this.rawLoading = true;
		const pollOneRaw = (date: string) =>
			this.tpl3plService.pollShipmentsRaw(date, date).pipe(map((res) => ({ date, res })));
		of(...dates).pipe(
			concatMap((date) => pollOneRaw(date)),
			reduce(
				(acc: { date: string; request_payload: any; response_b2b: any; response_b2c: any; success: boolean }[], { date, res }: { date: string; res: any }) => {
					acc.push({
						date,
						request_payload: res.request_payload ?? null,
						response_b2b: res.response_b2b ?? null,
						response_b2c: res.response_b2c ?? null,
						success: !!res.success
					});
					return acc;
				},
				[] as { date: string; request_payload: any; response_b2b: any; response_b2c: any; success: boolean }[]
			)
		).subscribe({
			next: (byDate) => {
				this.rawLoading = false;
				const apiCalls = byDate.flatMap((d) => {
					const arr = Array.isArray(d.request_payload) ? d.request_payload : (d.request_payload != null ? [d.request_payload] : []);
					return arr.map((req: any) =>
						req && typeof req === 'object' ? { date: d.date, ...req } : { date: d.date, request: req }
					);
				});
				const request_payload = {
					date_range: { from: dates[0], to: dates[dates.length - 1], days: dates.length },
					api_calls: apiCalls
				};
				const response = byDate.length === 1
					? { b2b: byDate[0].response_b2b, b2c: byDate[0].response_b2c }
					: { byDate: byDate.map((d) => ({ date: d.date, b2b: d.response_b2b, b2c: d.response_b2c, success: d.success })) };
				const content: any = { request_payload, response };
				this.dialog.open(RawResponseModalComponent, {
					width: '720px',
					maxWidth: '96vw',
					maxHeight: '90vh',
					data: {
						context: 'shipments',
						content,
						onSent: () => this.loadShipments()
					},
					panelClass: 'raw-response-dialog'
				});
			},
			error: () => {
				this.rawLoading = false;
				this.globalSearchService.showNotification('Could not load raw request/response', 'danger', 'bottom', 'right');
			}
		});
	}
}
